import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, changeLanguage } from '@/i18n';
import styles from './LanguageSwitcher.module.css';

/**
 * Selettore fra le 11 lingue (decisione 14).
 *
 * Pattern ARIA: bottone + `role="menu"` con `role="menuitemradio"`.
 * Le lingue sono un insieme di opzioni mutuamente esclusive, quindi
 * `menuitemradio` con `aria-checked`, non `menuitem` generici.
 *
 * Tastiera:
 *  · Enter/Spazio/↓  apre il menu e mette il focus sulla prima voce
 *  · ↑ ↓             scorre le voci
 *  · Home/End        prima / ultima
 *  · ESC             chiude e riporta il focus al bottone (WCAG 2.4.3)
 *  · Tab             chiude il menu (non lo si "attraversa")
 *
 * Il nome della lingua è SEMPRE nella lingua stessa (`lang_native`), con
 * l'attributo `lang` sull'elemento: uno screen reader italiano che legge
 * "日本語" deve cambiare voce, e lo fa solo se `lang="ja"` è dichiarato
 * (WCAG 3.1.2).
 */
export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const currentCode = i18n.resolvedLanguage ?? i18n.language ?? 'it';
  const current = LANGUAGES.find((l) => l.code === currentCode) ?? LANGUAGES[0]!;

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !document.getElementById(menuId)?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuId, open]);

  // All'apertura il focus va sulla lingua attiva.
  useEffect(() => {
    if (!open) return;
    const index = LANGUAGES.findIndex((l) => l.code === currentCode);
    itemRefs.current[Math.max(0, index)]?.focus();
  }, [currentCode, open]);

  const select = useCallback(
    async (code: string) => {
      await changeLanguage(code);
      close();
    },
    [close],
  );

  const onMenuKeyDown = (event: React.KeyboardEvent, index: number) => {
    let next: number | null = null;
    switch (event.key) {
      case 'ArrowDown':
        next = (index + 1) % LANGUAGES.length;
        break;
      case 'ArrowUp':
        next = (index - 1 + LANGUAGES.length) % LANGUAGES.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = LANGUAGES.length - 1;
        break;
      case 'Escape':
        event.preventDefault();
        close();
        return;
      case 'Tab':
        // Non si "attraversa" un menu con Tab: si chiude.
        setOpen(false);
        return;
      default:
        return;
    }
    event.preventDefault();
    itemRefs.current[next]?.focus();
  };

  return (
    <div className={styles.wrapper}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {/* La pill mostra solo «bandiera + IT» (compatta, come il
            riferimento), ma il nome accessibile resta esplicito:
            "Lingua: Italiano". Il solo codice "IT" non basta. */}
        <span className="ic-sr-only">{t('nav_lang', 'Lingua')}: </span>
        <span className={styles.flag} aria-hidden="true">
          {current.flag}
        </span>
        <span aria-hidden="true">{current.code.toUpperCase()}</span>
        <span className="ic-sr-only" lang={current.code}>
          {current.native}
        </span>
      </button>

      <ul id={menuId} role="menu" className={styles.menu} hidden={!open}>
        {LANGUAGES.map((language, index) => {
          const active = language.code === currentCode;
          return (
            <li key={language.code} role="none">
              <button
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                aria-current={active || undefined}
                className={styles.item}
                lang={language.code}
                onClick={() => void select(language.code)}
                onKeyDown={(event) => onMenuKeyDown(event, index)}
              >
                <span className={styles.flag} aria-hidden="true">
                  {language.flag}
                </span>
                <span>{language.native}</span>
                {active ? (
                  <span className={styles.check} aria-hidden="true">
                    ✓
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
